import { Injectable } from '@angular/core';
import { ClientService } from '../../client/client.service';
import { ProgramService } from '../../program/program.service';
import { ParticipantService } from '../../participant/participant.service';
import { ApplicationService } from '../../application/application.service';
import { ContentService } from '../../content/content.service';
import { User } from '../../../models/user/user.interface';
import { ContentOptions } from '../../../models/content/content-options.interface';
import {
	CLIENT_FEATURE_REFERENCE_CODES,
	constants,
	EdAccessConfig,
	EdGoalConfig,
	LocalStorageService,
	ParticipantUtilityService,
	PRODUCT_TYPES,
	PROGRAM_FEATURE_CODES,
	PROGRAM_TYPES
} from '@edassist/common';
import { ApplicationSearchResult, UserService } from '@edassist/api';
import { forkJoin, Observable, of, map } from 'rxjs';
import { switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class UserConfigService {
	constructor(
		private readonly clientService: ClientService,
		private readonly programService: ProgramService,
		private readonly participantService: ParticipantService,
		private readonly applicationService: ApplicationService,
		private readonly contentService: ContentService,
		private readonly userService: UserService
	) {}

	fetchEdGoalConfiguration(user: User): Observable<EdGoalConfig> {
		const isImpersonation = this.userService.getImpersonationEnabled();
		let activeProductId = LocalStorageService.getItem(constants.localStorageKeys.TOKENS_HYBRID)?.activeProduct;

		// if the url includes /tuition and activeProductId is not set, set activeProductId to 1
		if (!activeProductId && window.location.pathname.includes('tuition')) {
			activeProductId = constants.PRODUCTS.WORKFORCE_EDUCATION_ID;
		}

		return this.participantService.getParticipantEligibility(user.participantId, activeProductId, isImpersonation).pipe(
			switchMap((rulesOutput) => {
				const edGoalPrograms = rulesOutput?.programs?.filter(
					(program) => program.programType.id === PROGRAM_TYPES.edGoal
				);

				const isPathwayFeatureEnabled = this.programService.isAnyProgramFeatureEnabled(
					edGoalPrograms,
					constants.programFeatures.pathways.code
				);

				const isNonPathwayEgProgramExist = edGoalPrograms?.some(
					(edGoalProgram) =>
						!this.programService.isProgramFeatureEnabled(edGoalProgram, constants.programFeatures.pathways.code)
				);

				const edGoalsCalls = [
					this.applicationService.hasCompletedEdGoals(user.participantId, true),
					this.applicationService.hasCompletedEdGoals(user.participantId, false),
					this.applicationService.getParticipantApprovedEdGoals(user.participantId, activeProductId)
				];

				return forkJoin(edGoalsCalls).pipe(
					map(([edGoalsPathway, edGoalsNonPathway, approvedEdGoals]) => {
						const hasApprovedEdGoals = typeof approvedEdGoals === 'boolean' ? false : approvedEdGoals.result.length > 0;

						return {
							hasEdGoalPrograms:
								activeProductId === PRODUCT_TYPES.educationPathways ?
									isPathwayFeatureEnabled
								:	isNonPathwayEgProgramExist,
							hasEdGoalProgramsLoaded: true,
							hasEdGoalApps:
								activeProductId === PRODUCT_TYPES.educationPathways ? !!edGoalsPathway : !!edGoalsNonPathway,
							hasApprovedEdGoals,
							hasEligiblePrograms: rulesOutput?.programs?.length > 0
						} as EdGoalConfig;
					})
				);
			})
		);
	}

	fetchEdAccessConfiguration(): Observable<EdAccessConfig> {
		const isImpersonation = this.userService.getImpersonationEnabled();
		const clientId = ParticipantUtilityService.getClientLoginDetails()?.clientId;

		return this.clientService
			.isClientProgramFeatureEnabled(clientId, CLIENT_FEATURE_REFERENCE_CODES.EDUCATION_ACCESS, isImpersonation)
			.pipe(
				map((isEdAccessEnabled) => ({
					isEdAccessEnabled: !!isEdAccessEnabled
				}))
			);
	}

	fetchProgramEligibilityAndRelatedEdAccessConfiguration(user: User): Observable<EdAccessConfig> {
		const isImpersonation = this.userService.getImpersonationEnabled();

		return this.participantService.getProgramEligibility(user.participantId, isImpersonation).pipe(
			switchMap((rulesOutput) => {
				if (!rulesOutput?.programs?.length) {
					return of({}); // Return empty config if no programs
				}
				const programIds = rulesOutput.programs.map((program) => program.id).join(',');
				const programListForContent = `${programIds},${constants.CONTENT.program.clientDefault}`;
				const contentOptions: ContentOptions = {
					client: ParticipantUtilityService.getClientLoginDetails()?.clientId?.toString(),
					program: programListForContent,
					textOnly: true,
					cascade: false
				};

				const edAccessCalls = [
					this.programService.getEdAccessProgramConfigDetails(
						programIds,
						constants.programFeatures.edAccess.bannerConfig
					),
					this.programService.getEdAccessProgramConfigDetails(
						programIds,
						constants.programFeatures.edAccess.providerPageConfig
					),
					this.contentService.findByName(
						constants.contentConstants.contentTypes.general,
						constants.contentConstants.CONTENT_COMPONENTS.CLIENT_DOCUMENTS,
						constants.contentConstants.CONTENT_NAMES.PROGRAM_RESOURCES,
						contentOptions
					)
				];

				return forkJoin(edAccessCalls).pipe(
					map(([isEdAccessBannerDisabled, isEdAccessProviderPageDisabled, eligiblePolicyDocuments]) => ({
						isEdAccessBannerDisabled,
						isEdAccessProviderPageDisabled,
						hasEdAccessPrograms: this.programService.isAnyProgramFeatureEnabled(
							rulesOutput.programs,
							PROGRAM_FEATURE_CODES.ED_ACCESS
						),
						policyDocuments: eligiblePolicyDocuments
					}))
				);
			})
		);
	}
}
